import React, { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { v4 as uuidv4 } from "uuid"
import {
  Paper,
  IconButton,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SendIcon from "@mui/icons-material/Send"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import "./AISecretaryPanel.css"
import ReactMarkdown from "react-markdown"
import { AI_SECRETARY_WS_URL } from './config/serviceConfig';

const AISecretaryPanel = ({
  isLoggedIn,
  isVisible: propIsVisible,
  onClose,
  sessionId,
  archiveId,
  currentUser,
}) => {
  const { t } = useTranslation()
  const [aiSecretaryWs, setAISecretaryWs] = useState(null)
  const [isWsConnected, setIsWsConnected] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [isManuallyHidden, setIsManuallyHidden] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState({ x: window.innerWidth - 450, y: 100 })
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const panelRef = useRef(null)

  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState("")

  // 新增状态
  const [assistantsList, setAssistantsList] = useState([])
  const [selectedAgent, setSelectedAgent] = useState(null)

  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

  // 创建 WebSocket 连接
  useEffect(() => {
    // 只有在 archiveId 存在且面板可见时才创建连接
    if (!archiveId || !propIsVisible) return

    let ws
    try {
      ws = new WebSocket(AI_SECRETARY_WS_URL)
    } catch (error) {
      console.error("WebSocket connection error:", error)
      setIsWsConnected(false)
      return
    }

    ws.onopen = () => {
      console.log("AI Secretary WebSocket connection established")
      setAISecretaryWs(ws)
      setIsWsConnected(true)

      // 查询虚拟人列表
      const listAssistantsMessage = JSON.stringify({
        type: "list_assistants",
      })
      console.log("Sending list_assistants request:", listAssistantsMessage)
      ws.send(listAssistantsMessage)

      // 发送预热消息
      const warmupMessage = JSON.stringify({
        type: "query",
        sessionId: sessionId,
        archiveId: archiveId,
        query: "你好",
        agentName: assistantsList.length > 0 ? assistantsList[0] : "小秘书",
        unifiedUserId: String(currentUser?.unified_user_id || currentUser?.username || ""),
        userType: String(currentUser?.user_type || "campus"),
      })
      console.log("Sending warmup message:", warmupMessage)
      ws.send(warmupMessage)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log("AI Secretary received full message:", data)

        switch (data.type) {
          case "assistants_list":
            // 处理虚拟人列表
            const assistants = data.response.split(",")
            console.log("Received assistants list:", assistants)
            setAssistantsList(assistants)
            // 默认选择第一个虚拟人
            if (assistants.length > 0) {
              setSelectedAgent(assistants[0])
            }
            break
          case "response":
            // 直接显示所有消息
            setMessages((prev) => [
              ...prev,
              {
                id: uuidv4(),
                content: data.response,
                sender: "ai",
                timestamp: Date.now(),
              },
            ])
            break
          case "error":
            console.error("AI Secretary Error:", data.error)
            setMessages((prev) => [
              ...prev,
              {
                id: uuidv4(),
                content: data.error,
                sender: "error",
                timestamp: Date.now(),
              },
            ])
            break
          default:
            console.log("Unhandled message type:", data.type)
        }
      } catch (parseError) {
        console.error("Error parsing message:", parseError)
      }
    }

    ws.onerror = (error) => {
      console.error("Detailed AI Secretary WebSocket error:", {
        error: error,
        readyState: ws.readyState,
        url: ws.url,
      })
      setIsWsConnected(false)
    }

    ws.onclose = (event) => {
      console.log("AI Secretary WebSocket connection closed:", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      })
      setIsWsConnected(false)
    }

    // 重要：不关闭 WebSocket 连接
    return () => {
      console.log("Panel effect cleanup, but not closing WebSocket")
    }
  }, [archiveId, propIsVisible, sessionId, currentUser])

  // 发送消息
  const sendMessage = () => {
    if (!inputMessage.trim() || !aiSecretaryWs || !archiveId) {
      console.warn("Cannot send message:", {
        inputMessage: inputMessage.trim(),
        aiSecretaryWs: !!aiSecretaryWs,
        archiveId,
      })
      return
    }

    const userMessage = {
      id: uuidv4(),
      content: inputMessage,
      sender: "user",
      timestamp: Date.now(),
    }
    setMessages((prev) => [...prev, userMessage])

    // 确定 agentName，默认为第一个或"小秘书"
    const currentAgentName =
      selectedAgent ||
      (assistantsList.length > 0 ? assistantsList[0] : "小秘书")

    aiSecretaryWs.send(
      JSON.stringify({
        type: "query",
        sessionId: sessionId,
        archiveId: archiveId,
        query: inputMessage,
        agentName: currentAgentName,
        unifiedUserId: String(currentUser?.unified_user_id || currentUser?.username || ""),
        userType: String(currentUser?.user_type || "campus"),
      })
    )

    setInputMessage("")
  }

  // 渲染消息
  const renderMessages = () => {
    // 如果没有 archiveId，显示提示
    if (!archiveId) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#94a3b8",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <SmartToyIcon sx={{ fontSize: 48, color: "#64748b", marginBottom: "16px" }} />
          <Typography variant="h6" sx={{ marginBottom: "10px", color: "#fbbf24" }}>
            {t('ai_secretary_not_init')}
          </Typography>
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            {t('ai_secretary_after_start')}
          </Typography>
        </Box>
      )
    }

    // 如果 WebSocket 未连接，显示连接状态和错误提示
    if (!isWsConnected) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: "10px", color: "red" }}>
            {t('ai_secretary_connect_failed')}
          </Typography>
          <Typography variant="body2" sx={{ color: "#aaa" }}>
            {t('ai_secretary_connect_error')}
          </Typography>
          <Typography variant="body2" sx={{ color: "#aaa", marginTop: "10px" }}>
            {t('ai_secretary_retry')}
          </Typography>
        </Box>
      )
    }

    return messages.map((msg) => (
      <Box
        key={msg.id}
        sx={{
          textAlign: msg.sender === "user" ? "right" : "left",
          margin: "10px 0",
        }}
      >
        <Typography
          component="div"
          sx={{
            display: "inline-block",
            backgroundColor: msg.sender === "user" ? "#1976d2" : "#353535",
            color: "white",
            padding: "10px",
            borderRadius: "10px",
            maxWidth: msg.sender === "user" ? "70%" : "85%",
            wordBreak: "break-word",
          }}
        >
          {msg.sender === "ai" ? (
            <ReactMarkdown>{msg.content}</ReactMarkdown>
          ) : msg.sender === "error" ? (
            <Typography variant="body2" sx={{ color: "#ffcc00" }}>
              {t('ai_secretary_error_retry')}
            </Typography>
          ) : (
            msg.content
          )}
        </Typography>
      </Box>
    ))
  }

  // 原有的生命周期和件处理保持不变
  useEffect(() => {
    setIsVisible(propIsVisible)
    // 如果外部主动显示，重置手动隐藏状态
    if (propIsVisible) {
      setIsManuallyHidden(false)
    }
  }, [propIsVisible])

  // // 左边缘触发显示
  // useEffect(() => {
  //   const handleMouseMove = (e) => {
  //     // 只有在登录后、未被手动隐藏、未可见时才触发
  //     if (isLoggedIn && !isVisible && e.clientX > window.innerWidth -30) {
  //       setIsVisible(true)
  //     }
  //   }

  //   window.addEventListener("mousemove", handleMouseMove)
  //   return () => {
  //     window.removeEventListener("mousemove", handleMouseMove)
  //   }
  // }, [isLoggedIn, isManuallyHidden, isVisible])

  // 修改拖动事件处理
  const handleMouseDown = (e) => {
    if (e.target.closest(".panel-header")) {
      setIsDragging(true)
      const rect = panelRef.current.getBoundingClientRect()
      setOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      })
      e.preventDefault() // 防止文本选择
    }
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - offset.x;
    const newY = e.clientY - offset.y;

    // 获取面板尺寸
    const panelWidth = panelRef.current.offsetWidth;
    const panelHeight = panelRef.current.offsetHeight;

    // 设置边界限制
    const maxX = window.innerWidth - panelWidth - 20; // 右边预留20px边距
    const maxY = window.innerHeight - panelHeight - 20; // 底部预留20px边距

    // 确保面板始终在可视区域内
    setPosition({
      x: Math.min(Math.max(20, newX), maxX), // 左边预留20px边距
      y: Math.min(Math.max(20, newY), maxY)
    });

    e.preventDefault();
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // 在组件中添加这些事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      // 添加禁止选择文本的样式
      document.body.style.userSelect = "none"
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      // 移除禁止选择文本的样式
      document.body.style.userSelect = ""
    }
  }, [isDragging, offset])

  // 完全关闭面板
  const handleClose = (e) => {
    e.stopPropagation()
    setIsVisible(false)
    setIsManuallyHidden(true)
    onClose && onClose()

    // 不关闭 WebSocket 连接
  }

  // 如果未登录，不渲染面板
  if (!isLoggedIn) {
    return null
  }

  return (
    <div
      className={`ai-secretary-panel ${isVisible ? "visible" : "hidden"}`}
      ref={panelRef}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        position: "fixed",
        cursor: isDragging ? "grabbing" : "default",
        transform: "none", // 移除 transform
        transition: isDragging ? "none" : "all 0.1s ease", // 添加平滑过渡
        zIndex: 1000,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* 面板头部 */}
      <Box
        className="panel-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#2c2c2c",
          color: "white",
          padding: "10px",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <img
            src="/avatars/secretary.png"
            alt="AI Secretary"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              marginRight: "10px",
            }}
          />
          <Typography variant="subtitle1" sx={{ marginRight: "10px" }}>
            {t('ai_secretary_title')}
          </Typography>

          {/* 虚拟人选择下拉框 */}
          <FormControl
            size="small"
            sx={{
              width: "120px",
              marginRight: "10px",
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "white",
                },
              },
              "& .MuiSelect-icon": {
                color: "white",
              },
            }}
          >
            <Select
              value={selectedAgent || ""}
              onChange={(e) => setSelectedAgent(e.target.value)}
              disabled={assistantsList.length === 0}
              sx={{
                backgroundColor: "#353535",
                color: "white",
                "& .MuiSelect-select": {
                  padding: "5px 14px",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#353535",
                    color: "white",
                    maxWidth: "150px",
                    "& .MuiMenuItem-root": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "&:hover": {
                        backgroundColor: "#454545",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#555555",
                        "&:hover": {
                          backgroundColor: "#454545",
                        },
                      },
                    },
                  },
                },
              }}
            >
              {assistantsList.length === 0 ? (
                <MenuItem value="">{t('ai_secretary_loading')}</MenuItem>
              ) : (
                assistantsList.map((agent) => (
                  <MenuItem key={agent} value={agent}>
                    {agent}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>

        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* 消息区域 */}
      <Box
        className="messages-container"
        sx={{
          height: "400px",
          overflowY: "auto",
          backgroundColor: "#353535",
          padding: "10px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {renderMessages()}
        <div ref={messagesEndRef} />
      </Box>

      {/* 输入区域 */}
      <Box
        className="input-container"
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "10px",
          backgroundColor: "#2c2c2c",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
          placeholder={t('ai_secretary_placeholder')}
          disabled={!archiveId}
          style={{
            flex: 1,
            minHeight: "60px",
            backgroundColor: "rgba(30, 41, 59, 0.5)",
            color: "#e2e8f0",
            border: "1px solid rgba(148, 163, 184, 0.2)",
            borderRadius: "16px",
            padding: "12px 16px",
            resize: "none",
            fontSize: "14px",
            lineHeight: "1.5",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            outline: "none",
          }}
        />
        <button
          className="aibutton"
          onClick={sendMessage}
          disabled={!archiveId}
          style={{
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "10px",
            marginLeft: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: !archiveId ? 0.5 : 1,
          }}
        >
          <svg
            t="1735544059841"
            className="icon"
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            p-id="4242"
            width="16"
            height="16"
          >
            <path
              d="M899.925333 172.080762a48.761905 48.761905 0 0 1 0 28.525714l-207.969523 679.448381a48.761905 48.761905 0 0 1-81.115429 20.187429l-150.552381-150.552381-96.304762 96.329143a24.380952 24.380952 0 0 1-41.593905-17.237334v-214.966857l275.821715-243.370667-355.57181 161.596953-103.253333-103.228953a48.761905 48.761905 0 0 1 20.23619-81.091047L838.997333 139.702857a48.761905 48.761905 0 0 1 60.903619 32.353524z"
              p-id="4243"
              fill="#ffffff"
            />
          </svg>
        </button>
      </Box>
    </div>
  )
}

export default AISecretaryPanel
